import { useCallback, useRef, useState } from 'react';
import { PLAYBACK_MS } from '@roborally/engine';
import { frameFlags, frameRobots, frameTokens } from '../api.js';
import type { FlagView, FrameRobot, LogEntry, TokenView } from '../api.js';
import {
  afterArchiveStep, afterFlagStep, afterMissileStep, afterTokenStep, applyStep, ARCHIVE_STEPS,
  beatMs, expandMissileFlights, FLAG_STEPS, isTokenStep, MISSILE_STEPS, playableSteps, shotFrom,
} from './useSteppedPlayback.js';
import type { Archive, DisplayRobot, Shot, Step } from './useSteppedPlayback.js';

/**
 * Replaying a register the player has already watched, or missed.
 *
 * Everything needed is already stored: the events say what happened, and
 * `frame` says how the board stood going in. So this is entirely a
 * client-side matter -- no rule is re-evaluated and the server is not
 * asked to do anything.
 *
 * Deliberately separate from `useSteppedPlayback`, which follows the
 * LIVE game and is driven by state arriving. This one is driven by the
 * player asking. Keeping them apart means a replay cannot be mistaken for
 * the current position, and the live playback cannot be disturbed by
 * someone reviewing an earlier register.
 *
 * The token layer comes from the frame, not from the live game. Reviewing
 * turn 3 at turn 12 used to draw turn 12's debris under turn 3's robots,
 * which is the same fault the whole-game replay was built to avoid. A
 * frame written before the layer was recorded still has none, and the
 * caller falls back to the current board for those.
 */

export interface ReplayState {
  /** Positions to draw while replaying, or null when not. */
  positions: Map<string, DisplayRobot> | null;
  /** The shot to draw at this beat, or null where nothing fired. */
  shot: Shot | null;
  /** The step being shown, or null where none is. */
  step: Step | null;
  /**
   * The board as it stood, or null when not replaying AND when the frame
   * predates the token layer. Both mean "use the live board".
   */
  tokens: TokenView[] | null;
  /**
   * What else the frame said about each robot: powered down, virtual,
   * eliminated, where its archive stood.
   *
   * Null on a frame written before these were recorded. The distinction
   * has to be made per FRAME rather than per field: an absent
   * `poweredDown` means "not powered down" in a frame that records them
   * and "not written down" in one that does not, and the two want
   * opposite answers. A frame carrying a token layer is one of the new
   * ones, which is what this is keyed on.
   */
  flags: Map<string, FrameRobot> | null;
  /**
   * Robots destroyed earlier in the turn being replayed, as the last frame
   * that had them recorded them. Frames omit a destroyed robot, so without
   * this its panel loses the registers it played before it died. Empty
   * when replaying one entry.
   */
  fallen: Map<string, FrameRobot>;
  /**
   * Where the flags stood, walked along by the belts as the register
   * plays. Null when not replaying, and on a frame that does not say --
   * a course whose flags have never moved, or one written before they
   * were recorded. The course layout is what to draw for all three.
   *
   * Not the same thing as `flags` above, which is the robots.
   */
  courseFlags: FlagView[] | null;
  /**
   * Where each robot's archive stood, moved at step E as the register
   * plays. Null when not replaying.
   */
  archives: Map<string, Archive> | null;
  /** Which entry is being replayed, for labelling. */
  entry: LogEntry | null;
  /** The turn being replayed whole, or null when replaying one entry. */
  turn: number | null;
  /**
   * Every robot's registers as the turn being replayed revealed them by
   * its end, so the cards still to come can be drawn ahead of their
   * register. Null when not replaying a turn.
   */
  program: Map<string, NonNullable<FrameRobot['registers']>> | null;
  replaying: boolean;
  start: (entry: LogEntry) => void;
  /**
   * Replays a whole turn's registers: every entry given that has a frame
   * and a register, in log order. Each entry starts from its own frame,
   * so the board is exactly as recorded at every step of the turn.
   * `stepMs` is the time between steps, half a second unless given.
   */
  startTurn: (entries: LogEntry[], stepMs?: number) => void;
  stop: () => void;
  /**
   * Back to the start of the register being played, or of the one before
   * where it is already at the start. Paused stays paused. A whole turn's
   * replay only.
   */
  rewind: () => void;
  /** Holds the board where it stands; `resume` carries on from there. */
  paused: boolean;
  pause: () => void;
  resume: () => void;
}

/** Robots an earlier frame of `chosen`'s turn had and its own does not,
 * as they last stood. `queue` is oldest first. */
function fallenBefore(queue: LogEntry[], chosen: LogEntry): Map<string, FrameRobot> {
  const fallen = new Map<string, FrameRobot>();
  for (const e of queue) {
    if (e.seq >= chosen.seq) break;
    if (e.turn !== chosen.turn || !e.frame) continue;
    for (const r of frameRobots(e.frame)) fallen.set(r.id, r);
  }
  for (const r of frameRobots(chosen.frame!)) fallen.delete(r.id);
  return fallen;
}

/** One beat: wait `ms`, then do `run`. */
interface Beat { ms: number; run: () => void }

export function useReplay(
  /** The game's milliseconds between steps. */
  baseMs = PLAYBACK_MS,
): ReplayState {
  const [positions, setPositions] = useState<Map<string, DisplayRobot> | null>(null);
  const [tokens, setTokens] = useState<TokenView[] | null>(null);
  const [flags, setFlags] = useState<Map<string, FrameRobot> | null>(null);
  const [fallen, setFallen] = useState<Map<string, FrameRobot>>(new Map());
  const [courseFlags, setCourseFlags] = useState<FlagView[] | null>(null);
  const [archives, setArchives] = useState<Map<string, Archive> | null>(null);
  const [entry, setEntry] = useState<LogEntry | null>(null);
  const [turn, setTurn] = useState<number | null>(null);
  const [program, setProgram] = useState<ReplayState['program']>(null);
  // The shot being drawn this beat. One register can fire several, so it
  // is set by the step that fires and cleared by whatever follows.
  const [shot, setShot] = useState<Shot | null>(null);
  const [step, setStep] = useState<Step | null>(null);
  const [paused, setPaused] = useState(false);

  // The beats still to play, run one at a time so a pause can hold the
  // board between any two of them. `beat` is the one waiting on `timer`,
  // which a pause puts back at the front.
  const agenda = useRef<Beat[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const beat = useRef<Beat | null>(null);
  const held = useRef(false);
  // The turn being replayed, whole, so a rewind has somewhere to go back to.
  const turnQueue = useRef<{ queue: LogEntry[]; stepMs: number } | null>(null);

  const pump = useCallback(() => {
    if (timer.current || held.current) return;
    const next = agenda.current.shift();
    if (!next) return;
    beat.current = next;
    timer.current = setTimeout(() => {
      timer.current = null;
      beat.current = null;
      next.run();
      pump();
    }, next.ms);
  }, []);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    beat.current = null;
    agenda.current = [];
    held.current = false;
    setPaused(false);
  }, []);

  const pause = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (beat.current) agenda.current.unshift(beat.current);
    beat.current = null;
    held.current = true;
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    held.current = false;
    setPaused(false);
    pump();
  }, [pump]);

  const stop = useCallback(() => {
    clear();
    setPositions(null);
    setTokens(null);
    setFlags(null);
    setFallen(new Map());
    setCourseFlags(null);
    setArchives(null);
    setEntry(null);
    setTurn(null);
    setProgram(null);
    setShot(null);
    setStep(null);
  }, [clear]);

  /**
   * Plays the first entry of `queue`, then the rest. Every entry here has
   * a frame. Between entries the board holds one beat; after the last it
   * holds two before handing back.
   */
  const play = useCallback((queue: LogEntry[], stepMs = PLAYBACK_MS) => {
    const [chosen, ...rest] = queue;
    const next = rest.length > 0 ? () => play(rest, stepMs) : stop;
    const hold = rest.length > 0 ? stepMs : stepMs * 2;
    if (!chosen.frame) return;

    const running = new Map<string, DisplayRobot>(
      frameRobots(chosen.frame).map((r) => [
        r.id,
        { x: r.x, y: r.y, facing: r.facing, damage: r.damage ?? 0 },
      ]),
    );
    let layer = frameTokens(chosen.frame);
    let flagLayer = frameFlags(chosen.frame);
    let marks = new Map<string, Archive>(frameRobots(chosen.frame).map((r) => [
      r.id, { archiveMarker: r.archiveMarker, archiveFlag: r.archiveFlag },
    ]));

    setEntry(chosen);
    setPositions(new Map(running));
    setTokens(layer);
    setCourseFlags(flagLayer);
    setArchives(marks);
    // Keyed on the layer: both arrived in the same change, so a frame
    // with tokens is one that recorded these too.
    setFlags(layer ? new Map(frameRobots(chosen.frame).map((r) => [r.id, r])) : null);
    setFallen(layer && turnQueue.current ? fallenBefore(turnQueue.current.queue, chosen) : new Map());

    // A missile moves a TOKEN, and its events carry the FIRER's id -- so
    // handing one to `applyStep` would teleport the shooter to wherever
    // its shell went. With a layer to fly it across they are walked a
    // square at a time; without one there is nothing to show, so they are
    // dropped rather than spending a beat each on nothing.
    const steps = layer
      ? expandMissileFlights(playableSteps(chosen.events))
      : playableSteps(chosen.events).filter((step) => !MISSILE_STEPS.has(step.type));

    if (steps.length === 0) {
      // Nothing moved. Hold the frame briefly so the player sees the
      // board as it was, rather than a flicker.
      agenda.current.push({ ms: stepMs, run: next });
      pump();
      return;
    }

    let lastShot: Shot | null = null;
    steps.forEach((step, i) => {
      // A beat after the step before, longer after a shot.
      const ms = i === 0 ? stepMs : beatMs(steps[i - 1], stepMs);
      agenda.current.push({ ms, run: () => {
        // Every step, so a beat that fires nothing clears the last line.
        lastShot = shotFrom(step, running, lastShot);
        setShot(lastShot);
        setStep({ event: step, among: chosen.events });

        if (MISSILE_STEPS.has(step.type)) {
          if (layer) {
            layer = afterMissileStep(layer, step);
            setTokens(layer);
          }
        } else if (isTokenStep(step)) {
          // A drop, or a mine going off: the token layer only.
          if (layer) {
            layer = afterTokenStep(layer, step);
            setTokens(layer);
          }
        } else if (ARCHIVE_STEPS.has(step.type)) {
          // Step E, where the marker is taken -- and where the robot is
          // standing at this beat is where it is taken.
          marks = afterArchiveStep(marks, step, running.get(step.robotId));
          setArchives(marks);
        } else if (FLAG_STEPS.has(step.type)) {
          // A flag riding a belt, on the beat the belts run. Nothing to
          // move on a frame that did not record where the flags were: the
          // course's own are being drawn, and they do not move.
          flagLayer = afterFlagStep(flagLayer, step);
          if (flagLayer) setCourseFlags(flagLayer);
        } else {
          applyStep(running, step);
          setPositions(new Map(running));
        }

        // Hold the final position before moving on. TWO beats at the
        // end: the last step is the one worth looking at -- where a
        // robot ended up, what hit it -- and one beat is barely time to
        // register it before the live board cuts back in.
        if (i === steps.length - 1) {
          agenda.current.push({ ms: hold - stepMs + beatMs(step, stepMs), run: next });
        }
      } });
    });
    pump();
  }, [stop, pump]);

  const start = useCallback((chosen: LogEntry) => {
    clear();
    // Without a starting frame there is nothing to play from: the events
    // alone cannot say where a robot that did not move was standing.
    if (!chosen.frame) return;
    setTurn(null);
    setProgram(null);
    turnQueue.current = null;
    play([chosen], baseMs);
  }, [play, clear, baseMs]);

  const startTurn = useCallback((given: LogEntry[], stepMs = baseMs) => {
    clear();
    const queue = given
      .filter((e) => e.frame)
      .sort((a, b) => a.seq - b.seq);
    if (queue.length === 0) return;
    turnQueue.current = { queue, stepMs };
    setTurn(queue[0].turn);
    // Each register is revealed in its own frame, so the turn's frames
    // between them hold the whole program.
    const whole: NonNullable<ReplayState['program']> = new Map();
    for (const e of queue) {
      for (const r of frameRobots(e.frame!)) {
        const slots = whole.get(r.id) ?? [null, null, null, null, null];
        (r.registers ?? []).forEach((slot, i) => { if (slot) slots[i] = slot; });
        whole.set(r.id, slots);
      }
    }
    setProgram(whole);
    play(queue, stepMs);
  }, [play, clear, baseMs]);

  const rewind = useCallback(() => {
    if (!turnQueue.current || !entry) return;
    const { queue, stepMs } = turnQueue.current;
    const at = queue.findIndex((e) => e.seq === entry.seq);
    if (at < 0) return;
    const first = (register: number | null) => queue.findIndex((e) => e.register === register);
    let to = first(entry.register);
    if (to === at && entry.register !== null && entry.register > 1) to = first(entry.register - 1);
    const wasHeld = held.current;
    clear();
    if (wasHeld) {
      held.current = true;
      setPaused(true);
    }
    play(queue.slice(Math.max(0, to)), stepMs);
  }, [entry, clear, play]);

  return {
    positions, tokens, flags, fallen, courseFlags, archives, entry, turn, program, shot, step,
    replaying: positions !== null, start, startTurn, stop, rewind, paused, pause, resume,
  };
}
